// ClassroomContext.js
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import AuthContext from './AuthContext';
import { useNavigate } from 'react-router-dom';

export const ClassroomContext = createContext();

export const ClassroomContextProvider = ({ children }) => {
    const { userId } = useContext(AuthContext);
    const [classrooms, setClassrooms] = useState([]);
    const [joinedClassrooms, setJoinedClassrooms] = useState([]);
    const [classroom, setClassroom] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isTeacher, setIsTeacher] = useState(false);
    const [isOwner, setIsOwner] = useState(false);
    const [isFetchingClassroomDetails, setIsFetchingClassroomDetails] = useState(false);
    const [isFetchingAssignments, setIsFetchingAssignments] = useState(false);

    // assignment stuff
    const [classroomAssignments, setClassroomAssignments] = useState([]);
    const [classroomAssignment, setClassroomAssignment] = useState(null);
    const [myClassroomAssignments, setMyClassroomAssignments] = useState([]);
    const [allMyAssignments, setAllMyAssignments] = useState([]);
    const [classroomAssignmentSubmissions, setClassroomAssignmentSubmissions] = useState([]);
    const [classroomAssignmentSubmission, setClassroomAssignmentSubmission] = useState([]);
    


    const navigate = useNavigate();

    // --- NEW CLEARING FUNCTION ---
    const clearCurrentClassroom = useCallback(() => {
        setClassroom(null);
        setClassroomAssignments([]);
        setIsTeacher(false);
        setIsOwner(false);
        // setClassroomAssignment(null);
        // setClassroomAssignmentSubmissions([]);
        console.log("Current classroom data cleared.");
    }, []);



    const fetchClassrooms = useCallback(async (currentUserId) => {
        if (!currentUserId) return;
        setLoading(true);
        try {
            const [classroomsResponse, joinedResponse] = await Promise.all([
                axios.get(
                    `${process.env.REACT_APP_API_URL_LOCAL}/api/classrooms/user/${currentUserId}`
                ),
                axios.get(
                    `${process.env.REACT_APP_API_URL_LOCAL}/api/users/${currentUserId}/joined`
                ),
            ]);
            setClassrooms(classroomsResponse.data);
            setJoinedClassrooms(joinedResponse.data.joinedClassrooms);
        } catch (error) {
            console.error('Error fetching classrooms:', error);
            setClassrooms([]);
            setJoinedClassrooms([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (userId) {
            fetchClassrooms(userId);
        } else {
            setClassrooms([]);
            setJoinedClassrooms([]);
            setClassroom(null);
            setClassroomAssignments([]);
            setMyClassroomAssignments([]);
            setLoading(false);
        }
    }, [userId, fetchClassrooms]);

    const createClassroom = useCallback(async (classroomData) => {
        const formData = new FormData();
        formData.append('name', classroomData.name);
        if (classroomData.image) {
            formData.append('image', classroomData.image);
        }
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL_LOCAL}/api/classrooms`,
            formData
        );
        const newClassroom = response.data;
        setClassrooms((prevClassrooms) => [...prevClassrooms, newClassroom]);
        return newClassroom;
    }, []);

     const fetchClassroomAssignmentsInternal = useCallback(async (classroomIdToFetch) => {
        if (!classroomIdToFetch) {
            setClassroomAssignments([]);
            return { success: true, data: [] };
        }
        setIsFetchingAssignments(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL_LOCAL}/api/assignments/classroom/${classroomIdToFetch}`
            );
            setClassroomAssignments(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            console.error(`Error fetching assignments for classroom ${classroomIdToFetch}:`, error);
            setClassroomAssignments([]);
            return { success: false, error };
        } finally {
            setIsFetchingAssignments(false);
        }
    }, []);

    const fetchClassroom = useCallback(async (classroomIdToFetch, currentUserId) => {
        if (!classroomIdToFetch || !currentUserId) {
            clearCurrentClassroom();
            setIsFetchingClassroomDetails(false);
            return;
        }

        if (classroom?._id !== classroomIdToFetch) {

            clearCurrentClassroom();
        }
        setIsFetchingClassroomDetails(true);

        try {
            const classroomResponse = await axios.get(
                `${process.env.REACT_APP_API_URL_LOCAL}/api/classrooms/classroom/${classroomIdToFetch}`
            );
            const classroomData = classroomResponse.data;
            
            setClassroom(classroomData);
            setIsTeacher(classroomData.teachers.some(teacher => teacher._id === currentUserId));
            setIsOwner(classroomData.owner._id === currentUserId);

            await fetchClassroomAssignmentsInternal(classroomIdToFetch);

        } catch (error) {
            console.error(`Error fetching classroom ${classroomIdToFetch} or its assignments:`, error);
            clearCurrentClassroom();
        } finally {
            setIsFetchingClassroomDetails(false);
        }
    }, [classroom?._id, fetchClassroomAssignmentsInternal, clearCurrentClassroom]);

    const fetchClassroomAssignments = fetchClassroomAssignmentsInternal;

    const fetchMyClassroomAssignments = useCallback(async (classroomIdToFetch, currentUserId) => {
        if (!classroomIdToFetch || !currentUserId) {
            setMyClassroomAssignments([]);
            return;
        }
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL_LOCAL}/api/assignments/myclassroom/${classroomIdToFetch}/${currentUserId}`
            );
            setMyClassroomAssignments(response.data);
        } catch (error) {
            console.error('Error fetching my classroom assignments:', error);
            setMyClassroomAssignments([]);
        }
    }, []);

    const createAssignment = useCallback(async (classroomId, assignmentData, currentUserId) => {
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL_LOCAL}/api/assignments/${classroomId}`,
            assignmentData
        );
        const newAssignment = response.data;
        setClassroomAssignments(prev => [...prev, newAssignment]);
        if (assignmentData.students.includes(currentUserId)) {
            setMyClassroomAssignments(prev => [...prev, newAssignment]);
        }
        return newAssignment;
    }, []);

    const updateAssignment = useCallback(async (assignmentId, updateData) => {
        const response = await axios.patch(
            `${process.env.REACT_APP_API_URL_LOCAL}/api/assignments/${assignmentId}`,
            updateData
        );
        const updatedAssignment = response.data;
        setClassroomAssignments(prev => prev.map(a => a._id === assignmentId ? updatedAssignment : a));
        if (classroomAssignment?._id === assignmentId) {
            setClassroomAssignment(updatedAssignment);
        }
        setMyClassroomAssignments(prev => prev.map(a => a._id === assignmentId ? updatedAssignment : a));
        return updatedAssignment;
    }, [classroomAssignment]);

    const deleteAssignment = useCallback(async (assignmentId) => {
        try {
            await axios.delete(
                `${process.env.REACT_APP_API_URL_LOCAL}/api/assignments/${assignmentId}`
            );
            setClassroomAssignments(prev =>
                prev.filter(assignment => assignment._id !== assignmentId)
            );
            setMyClassroomAssignments(prev =>
                prev.filter(assignment => assignment._id !== assignmentId)
            );
            if (classroomAssignment && classroomAssignment._id === assignmentId) {
                setClassroomAssignment(null);
            }
        } catch (error) {
            console.error('Error deleting assignment:', error);
            throw error;
        }
    }, [classroomAssignment]);

    const fetchAssignment = useCallback(async (assignmentId) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL_LOCAL}/api/assignments/assignment/${assignmentId}`
            );
            const assignmentData = response.data;
            setClassroomAssignment(assignmentData);
        } catch (error) {
            console.error('Error fetching assignment:', error);
        }
    }, []);

    const updateClassroom = useCallback(async (id, updates) => {
        try {
            const classroomUpdateResponse = await axios.patch(
                `${process.env.REACT_APP_API_URL_LOCAL}/api/classrooms/${id}`,
                updates
            );

            const updatedClassroomFromServer = classroomUpdateResponse.data;

            setClassrooms(prevClassrooms =>
                prevClassrooms.map(c =>
                    c._id === id ? updatedClassroomFromServer : c
                )
            );
            setJoinedClassrooms(prevJoinedClassrooms =>
                prevJoinedClassrooms.map(jc =>
                    jc._id === id ? updatedClassroomFromServer : jc
                )
            );
            if (classroom && classroom._id === id) {
                setClassroom(updatedClassroomFromServer);
            }
        } catch (error) {
            console.error('Error updating classroom:', error);
            throw error;
        }
    }, [classrooms, joinedClassrooms, classroom]);

    const deleteClassroom = useCallback(async (classroomId) => {
        try {
            const response = await axios.delete(`${process.env.REACT_APP_API_URL_LOCAL}/api/classrooms/${classroomId}`);
            if (response.status === 200) {
                if (classroom && classroom._id === classroomId) {
                    setClassroom(null);
                }
                setClassrooms(prevClassrooms => prevClassrooms.filter(c => c._id !== classroomId));
                setJoinedClassrooms(prevJoinedClassrooms => prevJoinedClassrooms.filter(jc => jc._id !== classroomId));
                navigate("/my-classrooms");
            }
        } catch (error) {
            console.error("Failed to delete classroom:", error);
            throw error;
        }
    }, [navigate, classroom]);

    const createFolder = useCallback(async (folderData) => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL_LOCAL}/api/folders`,
                folderData
            );
            const newFolder = response.data;

            if (classroom && !folderData.parentFolderId && newFolder.classroom === classroom._id) {
                setClassroom(prevClassroom => ({
                    ...prevClassroom,
                    folders: [...(prevClassroom.folders || []), newFolder],
                }));
            }
            return newFolder;
        } catch (error) {
            console.error('Error creating folder:', error);
            throw error;
        }
    }, [classroom]);

    const updateFolder = useCallback(async (folderId, updates) => {
        try {
            const response = await axios.patch(
                `${process.env.REACT_APP_API_URL_LOCAL}/api/folders/${folderId}`,
                updates
            );
            const updatedFolder = response.data;

            if (classroom && !updatedFolder.parentFolder && updatedFolder.classroom === classroom._id) {
                 setClassroom(prevClassroom => {
                    if (!prevClassroom || !prevClassroom.folders) return prevClassroom;
                    return {
                        ...prevClassroom,
                        folders: prevClassroom.folders.map(folder =>
                            folder._id === folderId ? updatedFolder : folder
                        ),
                    };
                });
            }
            return updatedFolder;
        } catch (error) {
            console.error('Error updating folder:', error);
            throw error;
        }
    }, [classroom]);

    const deleteFolder = useCallback(async (folderId) => {
        try {
            let isTopLevelInCurrentClassroom = false;
            if (classroom && classroom.folders) {
                const folderInState = classroom.folders.find(f => f._id === folderId);
                if (folderInState && !folderInState.parentFolder) {
                    isTopLevelInCurrentClassroom = true;
                }
            }

            await axios.delete(
                `${process.env.REACT_APP_API_URL_LOCAL}/api/folders/${folderId}`
            );

            if (isTopLevelInCurrentClassroom && classroom) {
                 setClassroom(prevClassroom => {
                    if (!prevClassroom || !prevClassroom.folders) return prevClassroom;
                    return {
                        ...prevClassroom,
                        folders: prevClassroom.folders.filter(
                            (folder) => folder._id !== folderId
                        ),
                    };
                });
            }
        } catch (error) {
            console.error('Error deleting folder:', error);
            throw error;
        }
    }, [classroom]);

    // Memoizing for performance reasons
    const contextValue = React.useMemo(() => ({
        classrooms,
        joinedClassrooms,
        classroom,
        setClassroom, // Will check later to see if I actually need to pass this
        fetchClassrooms,
        createClassroom,
        fetchClassroom,
        updateClassroom,
        deleteClassroom,
        isOwner,
        setClassrooms,
        isTeacher,
        loading,
        isFetchingClassroomDetails,
        isFetchingAssignments,
        createFolder,
        updateFolder,
        deleteFolder,
        fetchClassroomAssignments,
        fetchMyClassroomAssignments,
        createAssignment,
        updateAssignment,
        deleteAssignment,
        classroomAssignments,
        myClassroomAssignments,
        classroomAssignment,
        setClassroomAssignment,
        allMyAssignments,
        classroomAssignmentSubmissions,
        setClassroomAssignmentSubmissions,
        classroomAssignmentSubmission,
        setClassroomAssignmentSubmission,
        clearCurrentClassroom,
    }), [
        classrooms, joinedClassrooms, classroom, fetchClassrooms, createClassroom, fetchClassroom,
        updateClassroom, deleteClassroom, isOwner, setClassrooms, isTeacher, loading, isFetchingClassroomDetails,
        isFetchingAssignments,
        createFolder, updateFolder, deleteFolder, fetchClassroomAssignments, fetchMyClassroomAssignments,
        createAssignment, updateAssignment, deleteAssignment, classroomAssignments, myClassroomAssignments,
        classroomAssignment, setClassroomAssignment, classroomAssignmentSubmissions,
        setClassroomAssignmentSubmissions, classroomAssignmentSubmission, setClassroomAssignmentSubmission,
        clearCurrentClassroom,
    ]);


    return (
        <ClassroomContext.Provider value={contextValue}>
            {children}
        </ClassroomContext.Provider>
    );
};
